import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { signIn, clearAnyExistingSession } from "../lib/auth";
import { Logo } from "../components/Logo";
import { AigenticoBadge } from "../components/AigenticoBadge";

// Human-friendly notices for the ?reason= query param we redirect to when the
// API client detects an unrecoverable auth failure.
const REASON_NOTICES: Record<string, string> = {
  expired: "Your session expired. Please sign in again to continue.",
  signed_out: "You have been signed out.",
};

export function SignIn() {
  const nav = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const [params] = useSearchParams();
  const reasonNotice = REASON_NOTICES[params.get("reason") ?? ""] ?? "";
  const returnTo = params.get("from") ?? location.state?.from ?? "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  // Clear any lingering session on mount so a stale signed-in state from a
  // previous browser tab or crashed session cannot block a fresh sign-in.
  // The submit button stays disabled until the reset completes to avoid a
  // race where the user submits before Amplify's in-memory session is gone.
  useEffect(() => {
    let cancelled = false;
    clearAnyExistingSession().finally(() => {
      if (!cancelled) setReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setError("");
    setBusy(true);
    try {
      // Belt-and-braces: reset again immediately before signIn to close any
      // remaining window where Amplify may have re-hydrated the session.
      await clearAnyExistingSession();
      const r = await signIn(email, password);
      if (r.ok) {
        nav(returnTo, { replace: true });
      } else {
        setError(`Additional step required: ${r.nextStep?.signInStep ?? "unknown"}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 py-6">
        <div className="flex items-center justify-between gap-4">
          <Link to="/"><Logo /></Link>
          <a href="https://aigentico.ai" target="_blank" rel="noopener" title="An Aigentico product — visit Aigentico" className="shrink-0">
            <AigenticoBadge size={32} />
          </a>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6">
          <div>
            <h2 className="mb-2">Sign in</h2>
            <p className="text-ink-soft text-sm">
              Don't have an account?{" "}
              <Link to="/signup" className="text-aigentico-navy font-medium hover:underline">Create one.</Link>
            </p>
          </div>

          {reasonNotice && (
            <div className="text-ink-soft text-sm border border-border bg-surface px-4 py-3 rounded-md">
              {reasonNotice}
            </div>
          )}

          <div>
            <label className="label" htmlFor="email">Email</label>
            <input
              id="email" type="email" autoComplete="email" required
              className="input" value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="password">Password</label>
            <input
              id="password" type="password" autoComplete="current-password" required
              className="input" value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <div className="mt-2 text-right">
              <Link to="/forgot" className="text-xs text-aigentico-navy hover:underline">Forgot password?</Link>
            </div>
          </div>

          {error && (
            <div className="text-aigentico-red text-sm border border-aigentico-red bg-white px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <button type="submit" disabled={busy || !ready} className="btn-primary w-full">
            {busy ? "Signing in…" : !ready ? "Preparing…" : "Sign in"}
          </button>
        </form>
      </main>
    </div>
  );
}
